// The in-scope player list for the Player Goals/Cards Movement `.player-toggle`.
//
// Both the control (`<ModuleControls>` renders the player <BentoSelect>) and the
// body (`PlayerMovement` resolves which series to draw) need the same derived
// list + selection, so the derivation lives here once. It combines the committed
// player fixtures (`playerMarketFixtures`) with the pure `@bento/shared` resolvers
// (scope filtering + selection clamping) — the window-free logic stays in shared.
//
// Scope filters which players are OFFERED (those in the paired static's rows for
// this scope + market type; "Match" = all players), NOT the drawn series.

import { resolvePlayersInScope, resolveSelectedPlayer } from "@bento/shared";
// The player list is a control concern needed synchronously at render time, so it
// reads a committed frontend fixture (not app-api). STOPGAP → M3 lineups/player
// feed; see playerMarketFixtures.ts.
import {
  playerGoalsMovementData,
  playerCardsMovementData,
  playerGoalsMarketData,
  playerCardsMarketData,
} from "./playerMarketFixtures.js";

export type PlayerMarketKind = "goals" | "cards";

/** Ordered in-scope player names for a Player Movement module. */
export function playerOptionsFor(
  kind: PlayerMarketKind,
  fixture: string | null | undefined,
  scope: string | null | undefined,
  marketType: string | null | undefined
): string[] {
  const fix = fixture || "Arsenal v Everton";
  const mtype = marketType || "Anytime";
  const sc = scope || "Match";
  const movData =
    kind === "goals" ? playerGoalsMovementData : playerCardsMovementData;
  const marketData =
    kind === "goals" ? playerGoalsMarketData : playerCardsMarketData;

  const fixMov = movData[fix] ?? movData["Arsenal v Everton"];
  const allTypeData = fixMov?.[mtype] ?? fixMov?.["Anytime"] ?? {};
  const allPlayers = Object.keys(allTypeData);
  const scopeNames =
    sc === "Match"
      ? null
      : (marketData[fix]?.[sc]?.[mtype] ?? []).map((r) => r.player);
  return resolvePlayersInScope(allPlayers, scopeNames);
}

/** The selection clamped to the current in-scope list (the derived default when
 *  the stored value isn't offered — mirrors the legacy first-option fallback). */
export function selectedPlayerFor(
  kind: PlayerMarketKind,
  fixture: string | null | undefined,
  scope: string | null | undefined,
  marketType: string | null | undefined,
  player: string | null | undefined
): string {
  return resolveSelectedPlayer(
    playerOptionsFor(kind, fixture, scope, marketType),
    player
  );
}
